import { type MultiActionQuery, type OpenApiSchemaCharacteristicStat } from '#shared/api/schema';
import { productStore } from './store';
import { type FnReducer } from './types';

type MergeState = {
    overlay: {
        isOpen: boolean;
    };
    checkStatus: {
        isChecked: boolean;
        errorMessage?: { title: string; description?: string };
        isOpen: boolean;
    };
    pageId?: string;
    allowedCharacteristics: OpenApiSchemaCharacteristicStat[];
    modalAddGroupingCharacteristic: {
        isOpen: boolean;
    };
    groupingCharacteristics: OpenApiSchemaCharacteristicStat[];
    modalGrid: {
        isOpen: boolean;
        errorMessage?: string;
    };
    modalPageId: {
        isOpen: boolean;
    };
    query: MultiActionQuery;
};

const initialState: MergeState = {
    checkStatus: {
        isChecked: false,
        isOpen: false,
    },
    allowedCharacteristics: [],
    modalAddGroupingCharacteristic: {
        isOpen: false,
    },
    groupingCharacteristics: [],
    modalGrid: {
        isOpen: false,
    },
    overlay: {
        isOpen: false,
    },
    modalPageId: {
        isOpen: false,
    },
    query: {},
};

const showOverlay: FnReducer<MergeState> = (state) => {
    state.overlay = {
        isOpen: true,
    };
};

const checkSuccessfully: FnReducer<
    MergeState,
    {
        characteristics: MergeState['allowedCharacteristics'];
        shouldChoosePageId: boolean;
        query: MergeState['query'];
    }
> = (state, { payload }) => {
    state.checkStatus = {
        isChecked: true,
        errorMessage: undefined,
        isOpen: false,
    };
    state.overlay = {
        isOpen: false,
    };
    state.allowedCharacteristics = payload.characteristics;
    state.groupingCharacteristics = [];
    state.query = payload.query;

    if (payload.shouldChoosePageId) {
        // Если не выбрали шаблон дизайна, то открываем модалку выбора шаблона дизайна
        state.modalPageId = {
            isOpen: true,
        };
    } else {
        //Сразу после выбора шаблона дизайна, загрузки и фиксации характеристик, открываем модалку добавления первой группирующей характеристики
        state.modalAddGroupingCharacteristic = {
            isOpen: true,
        };
    }

    state.modalGrid = {
        isOpen: false,
    };
};

const checkFailed: FnReducer<MergeState, Exclude<MergeState['checkStatus']['errorMessage'], undefined>> = (
    state,
    { payload }
) => {
    state.checkStatus = {
        isChecked: false,
        errorMessage: payload,
        isOpen: true,
    };
    state.overlay = {
        isOpen: false,
    };
};

const checkModalClose: FnReducer<MergeState> = (state) => {
    state.checkStatus = {
        isChecked: false,
        errorMessage: undefined,
        isOpen: false,
    };
};

const openModalAddGroupingCharacteristic: FnReducer<MergeState> = (state) => {
    state.modalAddGroupingCharacteristic = {
        isOpen: true,
    };
    state.modalGrid = {
        isOpen: false,
    };
};

const closeModalAddGroupingCharacteristic: FnReducer<MergeState> = (state) => {
    state.modalAddGroupingCharacteristic = {
        isOpen: false,
    };

    if (state.groupingCharacteristics.length > 0) {
        state.modalGrid = {
            isOpen: true,
        };
    }
};

const closeModalPageId: FnReducer<MergeState> = (state) => {
    state.modalPageId = {
        isOpen: false,
    };
};

const addPageId: FnReducer<MergeState, string> = (state, { payload }) => {
    state.pageId = payload;

    state.modalPageId = {
        isOpen: false,
    };

    state.modalAddGroupingCharacteristic = {
        isOpen: true,
    };
};

const addGroupingCharacteristic: FnReducer<MergeState, OpenApiSchemaCharacteristicStat> = (state, { payload }) => {
    state.groupingCharacteristics.push(payload);
    state.modalAddGroupingCharacteristic = {
        isOpen: false,
    };
    state.modalGrid = {
        isOpen: true,
    };
};

const removeGroupingCharacteristic: FnReducer<MergeState, OpenApiSchemaCharacteristicStat> = (state, { payload }) => {
    state.groupingCharacteristics = state.groupingCharacteristics.filter(
        (characteristic) => characteristic.meta?.id !== payload.meta?.id
    );
};

const closeModalGrid: FnReducer<MergeState> = (state) => {
    state.modalGrid = {
        isOpen: false,
    };
};

const mergeCompleted: FnReducer<MergeState> = (state) => {
    state.checkStatus = {
        isChecked: false,
        isOpen: false,
    };
    state.pageId = undefined;
    state.allowedCharacteristics = [];
    state.modalAddGroupingCharacteristic = {
        isOpen: false,
    };
    state.groupingCharacteristics = [];
    state.modalGrid = {
        isOpen: false,
    };
    state.modalPageId = {
        isOpen: false,
    };
};

const setErrorMessage: FnReducer<MergeState, MergeState['modalGrid']['errorMessage']> = (state, { payload }) => {
    state.modalGrid.errorMessage = payload;
};

const mergeVariantsSlice = productStore.createSlice({
    name: 'mergeVariants',
    initialState,
    reducers: {
        showOverlay,
        checkSuccessfully,
        checkFailed,
        checkModalClose,
        openModalAddGroupingCharacteristic,
        closeModalAddGroupingCharacteristic,
        closeModalPageId,
        addPageId,
        addGroupingCharacteristic,
        removeGroupingCharacteristic,
        closeModalGrid,
        mergeCompleted,
        setErrorMessage,
    },
});

export const {
    showOverlay: mergeProductVariantsShowOverlay,
    checkSuccessfully: mergeProductVariantsCheckSuccessfully,
    checkFailed: mergeProductVariantsCheckFailed,
    checkModalClose: mergeProductVariantsCheckModalClose,
    openModalAddGroupingCharacteristic: mergeProductVariantsOpenModalAddGroupingCharacteristic,
    closeModalAddGroupingCharacteristic: mergeProductVariantsCloseModalAddGroupingCharacteristic,
    closeModalPageId: mergeProductVariantsCloseModalPageId,
    addGroupingCharacteristic: mergeProductVariantsAddGroupingCharacteristic,
    removeGroupingCharacteristic: mergeProductVariantsRemoveGroupingCharacteristic,
    addPageId: mergeProductVariantsAddPageId,
    closeModalGrid: mergeProductVariantsCloseModalGrid,
    mergeCompleted: mergeProductVariantsMergeCompleted,
    setErrorMessage: mergeProductVariantsSetErrorMessage,
} = mergeVariantsSlice;

export const [useMergeProductVariantsCheckStatus] = mergeVariantsSlice.createSelector((state) => {
    return {
        isOpen: state.checkStatus.isOpen,
        isChecked: state.checkStatus.isChecked,
        errorMessage: state.checkStatus.errorMessage,
    };
});

export const [useMergeProductVariantsModalAddGroupingCharacteristic] = mergeVariantsSlice.createSelector((state) => {
    return {
        isOpen: state.modalAddGroupingCharacteristic.isOpen,
        groupingCharacteristics: state.groupingCharacteristics,
        allowedCharacteristics: state.allowedCharacteristics,
    };
});

export const [useMergeProductVariantsModalPageId] = mergeVariantsSlice.createSelector((state) => {
    return {
        isOpen: state.modalPageId.isOpen,
    };
});

export const [useMergeProductVariantsModalGrid] = mergeVariantsSlice.createSelector((state) => {
    return {
        isOpen: state.modalGrid.isOpen,
        countAllowedCharacteristics: state.allowedCharacteristics.length,
        groupingCharacteristics: state.groupingCharacteristics,
        errorMessage: state.modalGrid.errorMessage,
        pageId: state.pageId,
        query: state.query,
    };
});

export const [useMergeProductVariantsOverlay] = mergeVariantsSlice.createSelector((state) => {
    return {
        isOpen: state.overlay.isOpen,
    };
});
